import React, { useEffect, useState } from "react";
import { getFanUser } from "../lib/fanAccount";
import { SAVED_TALENT_EVENT, isSaved, refreshSavedLook, setSaved } from "../lib/savedTalent";
import { t } from "../i18n";
import "./SaveTalentHeart.css";

// The heart on a talent's fan page (1.5.3, founder 10/26): save this talent
// to the "Saved" row under Browse. Top-right of the card, the twin circle
// right under the "⋯" menu. Off = a grey line heart like the other corner
// icons; on = filled brand green (the GreenIcons heart's shape). Works for
// guests (device list) and signed-in fans (their account follows) — see
// lib/savedTalent. Taking the heart off takes the talent off the row.

const HEART = "M12 20.3 4.9 13a4.7 4.7 0 0 1 6.7-6.7l.4.5.4-.5A4.7 4.7 0 0 1 19.1 13Z";

type Props = {
  talent: { slug?: string; name?: string | null; avatarUrl?: string | null };
  /** The talent's own Preview: shown as fans see it, but not tappable. */
  disabled?: boolean;
};

export default function SaveTalentHeart({ talent, disabled }: Props) {
  const slug = talent.slug || "";
  const [on, setOn] = useState(() => !!slug && isSaved(slug));
  // The tap that turned it on gets the little pop; a page that opens with it on does not.
  const [popped, setPopped] = useState(false);

  useEffect(() => {
    if (!slug) return;
    const sync = () => setOn(isSaved(slug));
    sync();
    window.addEventListener(SAVED_TALENT_EVENT, sync);
    window.addEventListener("storage", sync);
    return () => {
      window.removeEventListener(SAVED_TALENT_EVENT, sync);
      window.removeEventListener("storage", sync);
    };
  }, [slug]);

  // A saved talent's page keeps the row's name and photo current.
  useEffect(() => {
    if (slug && !disabled) refreshSavedLook({ slug, name: talent.name, avatarUrl: talent.avatarUrl });
  }, [slug, talent.name, talent.avatarUrl, disabled]);

  if (!slug) return null;
  const name = talent.name || "";
  return (
    <button
      type="button"
      className={`rq-heart${on ? " rq-heart--on" : ""}${popped && on ? " rq-heart--pop" : ""}`}
      aria-pressed={on}
      aria-label={on ? t("saveTalentHeart.saved", { name }) : t("saveTalentHeart.save", { name })}
      disabled={disabled}
      onClick={() => {
        if (disabled) return;
        const next = !on;
        setOn(next);
        setPopped(next);
        setSaved({ slug, name: talent.name, avatarUrl: talent.avatarUrl }, next, getFanUser()?._id);
      }}
    >
      <svg width="16" height="16" viewBox="0 0 24 24" aria-hidden="true">
        <path d={HEART} fill={on ? "#24c961" : "none"} stroke={on ? "#24c961" : "currentColor"} strokeWidth="2" strokeLinejoin="round" />
      </svg>
    </button>
  );
}
